import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import SEO from '@/components/layout/SEO'

const M = '#06060c'
const G = '#d4a843'
const GR = '#22c55e'
const BR = 'rgba(255,255,255,0.07)'
const CA = 'rgba(255,255,255,0.04)'
const TX = 'rgba(255,255,255,0.88)'
const MU = 'rgba(255,255,255,0.45)'

export default function SponsorSuccessPage() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const planKey = searchParams.get('plan') || ''

  // Variant: 'pool' (one-time or recurring pool), 'sponsorship' (recurring
  // named), 'gift' (legacy direct gift). Default pool — Model A is pool-only.
  const type = searchParams.get('type') || 'pool'

  const isElite = planKey.includes('elite')
  const isBundle = planKey.includes('bundle')
  const months = planKey.includes('12m') ? 12 : planKey.includes('6m') ? 6 : 1

  const planLabel = isBundle
    ? t('sponsor.success.bundle', 'Full Bundle')
    : isElite
    ? t('sponsor.success.elite', 'Elite Academy')
    : t('sponsor.success.standard', 'Standard Curriculum')

  return (
    <div style={{ background: M, minHeight: '100vh', color: TX, fontFamily: "'Inter', system-ui, sans-serif", display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <SEO title="Sponsorship Confirmed" noindex />

      <motion.div
        initial={{ opacity: 0, scale: 0.94 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4 }}
        style={{ maxWidth: 560, width: '100%', margin: '0 auto', padding: '40px 24px', textAlign: 'center' }}
      >
        {/* Animated checkmark */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
          style={{ width: 80, height: 80, borderRadius: '50%', background: 'rgba(34,197,94,0.15)', border: '2px solid rgba(34,197,94,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px', fontSize: 36 }}
        >
          ✅
        </motion.div>

        <h1 style={{ fontSize: 32, fontWeight: 900, margin: '0 0 12px', fontFamily: "'Playfair Display', Georgia, serif", letterSpacing: '-0.5px' , color: '#0ea5e9'}}>
          {t('sponsor.success.title', 'You\'ve Changed a Life')}
        </h1>
        <p style={{ fontSize: 15, color: MU, lineHeight: 1.7, margin: '0 0 32px' }}>
          {type === 'sponsorship'
            ? "Your recurring sponsorship is live. Head to your dashboard to add the children you're funding — we'll send their invitations, and you'll watch their full learning journey."
            : type === 'gift'
            ? 'Your gift is confirmed. The family receives an email with their claim code.'
            : 'Your sponsorship is confirmed. Our team is assigning your seat to a verified child — their progress will appear on your dashboard.'}
        </p>

        {/* Match confirmation */}
        <div style={{ background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.25)', borderRadius: 18, padding: '20px 24px', marginBottom: 28, textAlign: 'left' }}>
          <p style={{ fontSize: 12, fontWeight: 800, color: GR, letterSpacing: '1.5px', marginBottom: 14 }}>
            {t('sponsor.success.matchTitle', 'YOUR SPONSORSHIP BREAKDOWN')}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[
              { label: t('sponsor.success.youContributed', 'Your contribution'), value: '50%', color: TX },
              { label: t('sponsor.success.sageMatched', 'Sage matched'), value: '50%', color: GR },
              { label: t('sponsor.success.childReceives', 'Child receives'), value: t('sponsor.success.fullAccess', 'Full access'), color: G },
            ].map(row => (
              <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 13, color: MU }}>{row.label}</span>
                <span style={{ fontSize: 14, fontWeight: 800, color: row.color }}>{row.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* What happens next */}
        <div style={{ background: CA, border: `1px solid ${BR}`, borderRadius: 18, padding: '20px 24px', marginBottom: 32, textAlign: 'left' }}>
          <p style={{ fontSize: 12, fontWeight: 800, color: MU, letterSpacing: '1.5px', marginBottom: 14 }}>
            {t('sponsor.success.nextTitle', 'WHAT HAPPENS NEXT')}
          </p>
          {(type === 'sponsorship'
            ? [
                "🤝 Add the children you're funding from your sponsor dashboard.",
                '📧 We send each child\u2019s invitation and handle their onboarding.',
                '📚 They start learning — full academy access, nothing held back.',
                '📊 You watch their lessons, quiz scores, and mastery climb.',
              ]
            : type === 'gift'
            ? [
                t('sponsor.success.next1', '📧 The family receives a gift email with their claim code.'),
                t('sponsor.success.next2', '🎓 They sign up or log in and enter the code.'),
                t('sponsor.success.next3', '📚 Full access is activated immediately — no payment needed from them.'),
                t('sponsor.success.next4', "📩 You'll receive a receipt at your email address."),
              ]
            : [
                '🌱 Our team assigns your seat to a verified child in need.',
                "📧 The child's family receives their invitation and onboarding.",
                '📚 Full academy access begins — Standard, Elite, and Bible Journey included.',
                '📊 Their progress appears on your sponsor dashboard as they learn.',
              ]
          ).map((step, i) => (
            <p key={i} style={{ fontSize: 13, color: MU, margin: '0 0 8px', lineHeight: 1.55 }}>{step}</p>
          ))}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate(type === 'gift' ? '/sponsor/give' : '/sponsor/dashboard')}
            style={{ background: `linear-gradient(135deg, ${GR}, #16a34a)`, color: '#fff', border: 'none', borderRadius: 14, padding: '16px', fontSize: 15, fontWeight: 800, cursor: 'pointer', boxShadow: '0 6px 24px rgba(34,197,94,0.3)' }}
          >
            {type === 'gift' ? '💚 Sponsor Another Child →' : '📊 Open Your Sponsor Dashboard →'}
          </motion.button>
          <button
            onClick={() => navigate('/')}
            style={{ background: CA, border: `1px solid ${BR}`, color: MU, borderRadius: 12, padding: '13px', fontSize: 14, cursor: 'pointer' }}
          >
            {t('sponsor.success.home', 'Back to Home')}
          </button>
        </div>
      </motion.div>
    </div>
  )
}
